import { useQueryClient } from '@tanstack/react-query';
import { Info, Lock, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useBlocker } from 'react-router';
import { isApiError, type Versioned } from '../api/client';
import { useApi } from '../api/context';
import { queryKeys, useAllCloudAccounts, useNetworkPage, useSecretStores } from '../api/queries';
import type {
  BlueprintDocument,
  DeletedVaultCheck,
  Deployment,
  DeploymentResource,
  Diagnostic,
  JsonObject,
  Provider,
  ResourcePlacement,
  ResourceType,
  Run,
  RunSummary,
  VaultOverview,
} from '../api/types';
import { isTerminalRunStatus } from '../api/types';
import { jsonText } from '../activity/describe';
import { usePermission } from '../auth/context';
import { ResourceIcon } from '../components/ResourceIcon';
import { useToast } from '../components/toast';
import { Button, Dialog, Field } from '../components/ui';
import { useOptionalDock } from '../dock/context';
import { describedBy } from '../lib/a11y';
import { formatDay } from '../lib/format';
import { createMergePatch } from '../lib/mergePatch';
import { impactSentences, joinNames } from '../lib/planReview';
import { diffLeaves, jsonEqual, mergeThreeWay, type MergeConflict } from '../lib/settingsMerge';
import { NetworkSection } from '../networks/deployment/NetworkSection';
import { networkErrorInfo, useNetworking } from '../networks/shared';
import { strings } from '../strings';
import { locationLabel, managedVaultLabel } from '../variables/registry';
import { dryRunProviders, useDebouncedDryRun } from './dryRun';
import {
  AccountFields,
  FailurePolicyField,
  ParameterField,
  RegionSelect,
  SecretStoreField,
  VaultFields,
  VaultModeField,
} from './fields';
import {
  checkForm,
  fieldLabel,
  fromForm,
  settingsDocument,
  SETTINGS_SECTIONS,
  toForm,
  type SettingsSection,
  usedProviders,
  type SettingsForm,
} from './settingsForm';

const s = strings.deploymentSettings;
const nd = strings.networks.deployment;
const MAX_RESENDS = 3;

function sectionOf(path: string): SettingsSection {
  if (path === '/name') return 'general';
  const group = path.split('/')[2];
  if (group === 'defaultPlacement' || group === 'placementOverrides') return 'placement';
  if (group === 'network') return 'network';
  if (group === 'parameters') return 'parameters';
  if (group === 'cloudAccounts') return 'accounts';
  if (group === 'vault' || group === 'secretStore') return 'vault';
  return 'failure';
}

function docOf(deployment: Deployment): JsonObject {
  return settingsDocument(deployment.name, deployment.settings);
}

function titleOf(key: SettingsSection): string {
  return key === 'network' ? nd.title : s.sections[key];
}

const isNetworkPath = (path: string) => path === '/settings/network' || path.startsWith('/settings/network/');

function conflictText(conflicts: readonly MergeConflict[]): string {
  return s.conflict(
    conflicts.map((c) => s.conflictField(fieldLabel(c.path), jsonText(c.theirs ?? null))).join('; '),
  );
}

/**
 * The deployment's Settings tab (spec 6.13, mock m14): sections with a nav, the effective placement and the
 * consequences of a move from the dry run, the fixed vault, a save bar, and the per-field merge after a 412.
 */
export function SettingsTab({
  deployment,
  doc,
  providers,
  resources,
  types,
  vaultType,
  vaultOverview,
  vaultPurged = null,
  activeApply = null,
  section,
  onSection,
  onSaved,
}: {
  deployment: Versioned<Deployment>;
  /** The blueprint's latest version. */
  doc: BlueprintDocument;
  providers: Provider[];
  resources: DeploymentResource[];
  types: ReadonlyMap<string, ResourceType>;
  /** The vault's location type (the vault overview's `type`). */
  vaultType: string | null | undefined;
  /** Feature spec 15: the vault overview (retention, `outlivesVault`), when loaded. */
  vaultOverview?: VaultOverview | null;
  /** A re-read found the soft-deleted vault purged outside Nebisys (`Deployment.vaultCheck`). */
  vaultPurged?: DeletedVaultCheck | null;
  /** The deployment's active run when it is an apply: it may create the vault in its snapshot's placement. */
  activeApply?: Run | null;
  section: SettingsSection | null;
  onSection: (section: SettingsSection) => void;
  onSaved: (deployment: Versioned<Deployment>) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const dock = useOptionalDock();
  const queryClient = useQueryClient();
  const operate = usePermission('operate');
  const admin = usePermission('admin');
  const accounts = useAllCloudAccounts(operate);
  const stores = useSecretStores(operate);
  const ids = useId();
  const definitions = useMemo(() => doc.parameters ?? {}, [doc.parameters]);
  const [base, setBase] = useState(deployment);
  const [form, setFormState] = useState<SettingsForm>(() =>
    toForm(deployment.data.name, deployment.data.settings, definitions),
  );
  const [seen, setSeen] = useState(deployment);
  const [message, setMessage] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [apiDiagnostics, setApiDiagnostics] = useState<Diagnostic[]>([]);
  const [vaultProblem, setVaultProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  // Feature spec 17: a network Problem of the last save, and the runs a saved detach created.
  const [networkProblem, setNetworkProblem] = useState<{ code: string; message: string } | null>(null);
  const [detach, setDetach] = useState<{ network: string; runs: RunSummary[] } | null>(null);
  const networking = useNetworking();
  const networkPage = useNetworkPage(null, networking.enabled && operate);
  const networkNames = new Map((networkPage.data?.items ?? []).map((x) => [x.id, x.name]));
  const showNetwork = networking.enabled || !!base.data.network;
  const sections = SETTINGS_SECTIONS.filter((key) => key !== 'network' || showNetwork);
  const summaryRef = useRef<HTMLDivElement>(null);
  const setForm = (update: (form: SettingsForm) => SettingsForm) => {
    setFormState(update);
    setApiDiagnostics([]);
    setNetworkProblem(null);
  };
  const update = (patch: Partial<SettingsForm>) => setForm((f) => ({ ...f, ...patch }));

  const fixed = !!base.data.vaultPlacementFixed;
  const vault = base.data.vault ?? null;
  // An apply in progress may create the vault in its snapshot's placement (spec 6.13 item 6, code review C5).
  const runVaultRegion =
    !fixed && activeApply && !isTerminalRunStatus(activeApply.status)
      ? (activeApply.settings.vault?.region ?? activeApply.settings.defaultPlacement?.region ?? '')
      : null;
  const formSettings = (f: SettingsForm, used: readonly string[] | null = null) =>
    fromForm(f, doc, definitions, { used, fixedVault: fixed, allAccounts: true });
  const baseDoc = docOf(base.data);
  const formDoc = settingsDocument(form.name, formSettings(form));
  const changes = diffLeaves(baseDoc, formDoc);
  const changed = new Set(changes.map((c) => sectionOf(c.path)));

  // Leaving the page with unsaved changes asks first (6.13 item 10); the page's own tabs and sections don't.
  const unsaved = operate && changes.length > 0;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      unsaved &&
      currentLocation.pathname !== nextLocation.pathname &&
      !(nextLocation.state as { discardSettings?: boolean } | null)?.discardSettings,
  );
  useEffect(() => {
    if (!unsaved) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [unsaved]);

  // Rebase (6.13 item 9): the deployment changed while the tab is open (a parameter edit in the Variables window,
  // a run starting, someone else's save picked up by a refetch).
  // Another deployment (the page is keyed, this is the tab's own guard, code review C4): start over from it.
  // An older revision than the base (a poll answer that lands after a save) is ignored (C18).
  if (deployment !== seen && deployment.data.id !== base.data.id) {
    setSeen(deployment);
    setBase(deployment);
    setFormState(toForm(deployment.data.name, deployment.data.settings, definitions));
    setMessage(null);
    setSubmitted(false);
    setApiDiagnostics([]);
    setVaultProblem(null);
    setEditing(null);
  } else if (deployment !== seen && deployment.data.revision < base.data.revision) {
    setSeen(deployment);
  } else if (deployment !== seen) {
    setSeen(deployment);
    if (deployment.data.revision !== base.data.revision || !jsonEqual(docOf(deployment.data), baseDoc)) {
      const theirs = docOf(deployment.data);
      if (jsonEqual(theirs, baseDoc) || changes.length === 0) {
        setBase(deployment);
        if (!jsonEqual(theirs, baseDoc))
          setFormState(toForm(deployment.data.name, deployment.data.settings, definitions));
      } else {
        const merged = mergeThreeWay<JsonObject>(baseDoc, formDoc, theirs);
        const next = merged.merged as unknown as { name: string; settings: Deployment['settings'] };
        setBase(deployment);
        setFormState(toForm(next.name, next.settings, definitions));
        setMessage(merged.conflicts.length ? conflictText(merged.conflicts) : s.rebased);
      }
    }
  }

  const dry = useDebouncedDryRun(
    base.data.blueprintId,
    base.data.id,
    operate ? { name: form.name, settings: formSettings(form) } : null,
  );
  const used = dryRunProviders(dry.data) ?? usedProviders(form, doc);
  const browserErrors = checkForm(form, doc, definitions, used);
  const settingsDiagnostics = [
    ...apiDiagnostics,
    ...(dry.data?.diagnostics ?? []).filter(
      (d) => d.severity === 'error' && (d.path === '/name' || d.path?.startsWith('/settings/')),
    ),
  ];
  const errorsAt = (pointer: string) => [
    ...(submitted ? (browserErrors[pointer] ?? []) : []),
    ...settingsDiagnostics.filter((d) => d.path === pointer).map((d) => d.message),
  ];

  // `?section=` scrolls to the section once.
  useEffect(() => {
    if (section) document.getElementById(`${ids}-${section}`)?.scrollIntoView?.({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldId = (pointer: string): string => {
    const parts = pointer.split('/');
    if (pointer === '/name') return `${ids}-name`;
    if (parts[2] === 'defaultPlacement') return `${ids}-default-${parts[3] ?? 'provider'}`;
    if (parts[2] === 'parameters') return `${ids}-param-${parts[3]}`;
    if (parts[2] === 'cloudAccounts') return `${ids}-account-${parts[3]}`;
    if (parts[2] === 'vault') return `${ids}-vault-${parts[3] ?? 'provider'}`;
    if (parts[2] === 'secretStore') return `${ids}-store`;
    if (parts[2] === 'network') return `${ids}-network`;
    return `${ids}-${sectionOf(pointer)}`;
  };
  const summaryItems = [
    ...Object.entries(browserErrors).flatMap(([pointer, messages]) => messages.map((m) => ({ pointer, m }))),
    ...settingsDiagnostics.map((d) => ({ pointer: d.path, m: d.message })),
  ];

  const save = async () => {
    setSubmitted(true);
    if (Object.keys(browserErrors).length > 0) {
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }
    const mine = formDoc;
    let target = base;
    const patch = createMergePatch(docOf(target.data), mine);
    if (Object.keys(patch).length === 0) return;
    setBusy(true);
    setMessage(null);
    setVaultProblem(null);
    setNetworkProblem(null);
    const detachingFrom = base.data.network?.mode === 'attached' ? base.data.network.name : null;
    try {
      for (let attempt = 0; ; attempt++) {
        try {
          const saved = await api.patchDeployment(target.data.id, patch, target.etag);
          // A detach answers with the detach run and the system apply (feature spec 17).
          if (saved.data.runs?.length) {
            setDetach({ network: detachingFrom ?? saved.data.network?.name ?? '', runs: saved.data.runs });
            void queryClient.invalidateQueries({ queryKey: ['runs'] });
          }
          setBase(saved);
          setSeen(saved);
          setFormState(toForm(saved.data.name, saved.data.settings, definitions));
          setSubmitted(false);
          onSaved(saved);
          dock?.announce(s.saved);
          toast.show({ tone: 'success', title: s.saved });
          return;
        } catch (error) {
          if (isApiError(error, 'PRECONDITION_FAILED') && attempt < MAX_RESENDS) {
            const fresh = await api.getDeployment(target.data.id);
            queryClient.setQueryData(queryKeys.deployment(fresh.data.id), fresh);
            if (jsonEqual(docOf(fresh.data), docOf(target.data))) {
              target = fresh;
              setBase(fresh);
              setSeen(fresh);
              continue;
            }
            const merged = mergeThreeWay<JsonObject>(docOf(target.data), mine, docOf(fresh.data));
            const next = merged.merged as unknown as { name: string; settings: Deployment['settings'] };
            setBase(fresh);
            setSeen(fresh);
            setFormState(toForm(next.name, next.settings, definitions));
            setMessage(merged.conflicts.length ? conflictText(merged.conflicts) : s.rebased);
            return;
          }
          if (
            isApiError(error) &&
            'network' in ((patch.settings as JsonObject | undefined) ?? {}) &&
            (networkErrorInfo(error.code) || error.code === 'RUN_IN_PROGRESS')
          ) {
            setNetworkProblem({ code: error.code, message: error.detail ?? error.message });
            requestAnimationFrame(() => document.getElementById(`${ids}-network`)?.scrollIntoView?.());
          } else if (isApiError(error, 'VAULT_PLACEMENT_FIXED')) {
            setApiDiagnostics(error.diagnostics);
            setVaultProblem(error.detail ?? null);
            requestAnimationFrame(() => document.getElementById(`${ids}-vault`)?.scrollIntoView?.());
          } else if (isApiError(error) && error.diagnostics.length > 0) {
            setApiDiagnostics(error.diagnostics);
            requestAnimationFrame(() => summaryRef.current?.focus());
          } else toast.error(error);
          return;
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const discard = () => {
    setFormState(toForm(base.data.name, base.data.settings, definitions));
    setSubmitted(false);
    setApiDiagnostics([]);
    setVaultProblem(null);
    setNetworkProblem(null);
    setMessage(null);
  };

  // ---- the effective placement (6.13 item 3)
  const providerOf = (key: string | null | undefined) => providers.find((p) => p.key === key);
  const regionName = (provider: string | null | undefined, region: string | null | undefined) =>
    region ? (providerOf(provider)?.regions.find((r) => r.name === region)?.displayName ?? region) : '';
  const placements: ResourcePlacement[] | null = operate ? (dry.data?.resources ?? null) : null;
  const replaced = (placements ?? []).filter((r) => r.replaced);
  const regions = [...new Set(replaced.map((r) => r.region))];
  const moveRegion = regions.length === 1 ? (regions[0] ?? null) : null;
  const resolution = (dry.data?.diagnostics ?? []).filter(
    (d) => d.path?.startsWith('/connections/') || (d.path?.startsWith('/resources/') && d.resourceId),
  );
  const statefulReplaced = replaced.some((r) => r.stateful);
  const appRegions = (placements ?? [])
    .filter((r) => types.get(r.type)?.injectionProperty && r.region && vault && r.region !== vault.region)
    .map((r) => r.region!);

  const sectionTitle = titleOf;

  const placementTable = () => {
    if (!operate) {
      return (
        <table className="table mt-3">
          <thead>
            <tr>
              <th scope="col">{strings.deploymentPage.resource}</th>
              <th scope="col">{strings.settings.provider}</th>
              <th scope="col">{strings.settings.region}</th>
            </tr>
          </thead>
          <tbody>
            {resources.map((r) => (
              <tr key={r.resourceId}>
                <td className="font-mono">{r.resourceId}</td>
                <td>{r.provider}</td>
                <td>{r.region}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (!placements) {
      return <p className="help mt-3">{dry.error ? s.checkFailed : s.checking}</p>;
    }
    return (
      <div className="table-scroll mt-3">
        <table className="table" aria-label={s.effective} data-testid="effective-placement">
          <thead>
            <tr>
              <th scope="col">{strings.deploymentPage.resource}</th>
              <th scope="col">{strings.settings.provider}</th>
              <th scope="col">{strings.settings.region}</th>
              <th scope="col">{s.comesFrom}</th>
              <th scope="col">
                <span className="sr-only">{strings.deploymentPage.actions}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {placements.flatMap((r) => {
              const was = r.applied ?? r.current;
              const moved = !!was && !!r.region && was.region !== r.region;
              const override = form.overrides[r.resourceId];
              const hasOverride = !!(override?.provider || override?.region);
              const rows = [
                <tr key={r.resourceId} data-testid={`placement-${r.resourceId}`}>
                  <td>
                    <span className="flex items-center gap-2">
                      <ResourceIcon name={types.get(r.type)?.icon} className="size-4 text-fg-muted" />
                      <span className="font-mono">{r.resourceId}</span>
                    </span>
                  </td>
                  <td>{r.provider ?? strings.common.dash}</td>
                  <td>
                    <span className={moved ? 'font-semibold' : undefined}>
                      {r.region ?? strings.common.dash}
                    </span>
                    {moved && <span className="ml-1 text-xs text-fg-muted">{s.was(was!.region ?? '')}</span>}
                  </td>
                  <td>{s.sources[r.regionSource]}</td>
                  <td className="text-right">
                    <Button
                      className="btn-sm"
                      aria-expanded={editing === r.resourceId}
                      aria-label={s.overrideFor(r.resourceId)}
                      onClick={() => setEditing(editing === r.resourceId ? null : r.resourceId)}
                    >
                      {hasOverride ? s.editOverride : s.override}
                    </Button>
                  </td>
                </tr>,
              ];
              if (editing === r.resourceId) {
                const value = override ?? { provider: '', region: '' };
                const resource = doc.resources.find((x) => x.id === r.resourceId);
                const set = (next: { provider: string; region: string }) =>
                  setForm((f) => ({ ...f, overrides: { ...f.overrides, [r.resourceId]: next } }));
                rows.push(
                  <tr key={`${r.resourceId}-edit`} className="settings-override">
                    <td colSpan={5}>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="label" htmlFor={`${ids}-ovp-${r.resourceId}`}>
                            {s.providerOf(r.resourceId)}
                          </label>
                          <select
                            id={`${ids}-ovp-${r.resourceId}`}
                            className="input"
                            value={value.provider}
                            onChange={(e) => set({ provider: e.target.value, region: '' })}
                          >
                            <option value="">{strings.common.notSet}</option>
                            {providers
                              .filter((p) => !resource || p.supportedTypes.includes(resource.type))
                              .map((p) => (
                                <option key={p.key} value={p.key}>
                                  {p.displayName}
                                </option>
                              ))}
                          </select>
                        </div>
                        <div>
                          <label className="label" htmlFor={`${ids}-ovr-${r.resourceId}`}>
                            {s.regionOf(r.resourceId)}
                          </label>
                          <RegionSelect
                            id={`${ids}-ovr-${r.resourceId}`}
                            provider={providerOf(value.provider || r.provider)}
                            value={value.region}
                            onChange={(region) => set({ ...value, region })}
                          />
                        </div>
                        <Button
                          onClick={() => {
                            set({ provider: '', region: '' });
                            setEditing(null);
                          }}
                        >
                          {s.removeOverride}
                        </Button>
                        <Button onClick={() => setEditing(null)}>{s.doneOverride}</Button>
                      </div>
                    </td>
                  </tr>,
                );
              }
              return rows;
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const changeText = (c: (typeof changes)[number]) => {
    // Feature spec 15 9.8: the vault-mode switch names the next steps.
    if (c.path === '/settings/vaultMode') {
      const store = form.secretStore || 'default';
      if (c.after === 'store') {
        const label = locationLabel({
          type: vaultType ?? '',
          secret: true,
          label: vaultOverview?.label ?? '',
          container: null,
        });
        return fixed && vault?.state === 'live' && vaultOverview?.outlivesVault
          ? strings.vaults.saveToStoreDeleting(store, label, vaultOverview.deletedRetentionDays ?? 0)
          : strings.vaults.saveToStore(store);
      }
      if (c.after === 'managed') {
        const provider =
          form.vaultPlacementChoice === 'custom' && form.vaultProvider
            ? form.vaultProvider
            : form.defaultProvider;
        return strings.vaults.saveToManaged(managedVaultLabel(provider));
      }
    }
    return `${fieldLabel(c.path)} ${jsonText(c.before ?? null)} → ${jsonText(c.after ?? null)}`;
  };
  // The network's leaves are one change in words (feature spec 17): "attaches dev to Non-production".
  const networkWords = (): string | null => {
    if (!changes.some((c) => isNetworkPath(c.path))) return null;
    const before = base.data.settings.network?.networkId ?? null;
    const after = form.network?.networkId || null;
    const dev = form.name || base.data.name;
    const nameOf = (id: string) =>
      base.data.network?.id === id ? base.data.network.name : (networkNames.get(id) ?? id);
    if (after && before !== after) return nd.saveBar.attaches(dev, nameOf(after));
    if (before && !after) return nd.saveBar.detaches(dev, nameOf(before));
    return nd.saveBar.resizes(dev, nameOf(after ?? before ?? ''));
  };
  const barTexts = [
    ...(networkWords() ? [networkWords()!] : []),
    ...changes.filter((c) => !isNetworkPath(c.path)).map(changeText),
  ];
  const accountName = (id: string | undefined) =>
    accounts.data?.find((a) => a.id === id)?.name ?? id ?? strings.common.dash;

  return (
    <div className="settings-layout">
      <nav aria-label={s.nav} className="settings-nav">
        <ul>
          {sections.map((key) => (
            <li key={key}>
              <button
                type="button"
                className={`settings-nav-item ${section === key ? 'is-current' : ''}`}
                aria-label={changed.has(key) ? s.sectionChanged(sectionTitle(key)) : undefined}
                onClick={() => {
                  onSection(key);
                  document.getElementById(`${ids}-${key}`)?.scrollIntoView?.({ block: 'start' });
                }}
              >
                {sectionTitle(key)}
                {changed.has(key) && <span aria-hidden className="settings-dot" />}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Disabled while a save runs, so nothing typed meanwhile is lost when the saved settings come back (C18). */}
      <fieldset className="settings-sections" disabled={busy} aria-busy={busy || undefined}>
        {!operate && <p className="callout callout-info">{s.readOnly}</p>}
        {message && (
          <p role="status" className="callout callout-warning" data-testid="settings-conflict">
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            {message}
          </p>
        )}
        {submitted && summaryItems.length > 0 && (
          <div ref={summaryRef} tabIndex={-1} role="alert" className="callout callout-danger">
            <div>
              <p className="font-semibold">{s.errorSummary(summaryItems.length)}</p>
              <ul className="mt-1 list-disc pl-5">
                {summaryItems.map((item, i) => (
                  <li key={i}>
                    <a
                      className="link"
                      href={`#${fieldId(item.pointer)}`}
                      onClick={(event) => {
                        event.preventDefault();
                        document.getElementById(fieldId(item.pointer))?.focus();
                      }}
                    >
                      {fieldLabel(item.pointer)}: {item.m}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <SettingsCard ids={ids} changed={changed} id="general">
          {operate ? (
            <Field
              label={strings.settings.name}
              htmlFor={`${ids}-name`}
              help={strings.settings.nameHelp}
              errors={errorsAt('/name')}
              required
            >
              <input
                id={`${ids}-name`}
                className="input max-w-sm"
                value={form.name}
                maxLength={64}
                aria-invalid={errorsAt('/name').length ? true : undefined}
                aria-describedby={describedBy(`${ids}-name`, true, errorsAt('/name').length > 0)}
                onChange={(e) => update({ name: e.target.value })}
              />
            </Field>
          ) : (
            <dl className="settings-dl">
              <dt>{strings.settings.name}</dt>
              <dd>{base.data.name}</dd>
            </dl>
          )}
        </SettingsCard>

        <SettingsCard ids={ids} changed={changed} id="placement">
          {operate ? (
            <div className="grid max-w-2xl gap-3 sm:grid-cols-2">
              <Field
                label={s.defaultProvider}
                htmlFor={`${ids}-default-provider`}
                errors={errorsAt('/settings/defaultPlacement/provider')}
              >
                <select
                  id={`${ids}-default-provider`}
                  className="input"
                  value={form.defaultProvider}
                  onChange={(e) => update({ defaultProvider: e.target.value, defaultRegion: '' })}
                >
                  <option value="">{strings.settings.noProvider}</option>
                  {providers.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={s.defaultRegion}
                htmlFor={`${ids}-default-region`}
                errors={errorsAt('/settings/defaultPlacement/region')}
                help={
                  form.defaultRegion !== (base.data.settings.defaultPlacement?.region ?? '')
                    ? s.was(
                        regionName(
                          base.data.settings.defaultPlacement?.provider,
                          base.data.settings.defaultPlacement?.region,
                        ) || s.unset,
                      )
                    : undefined
                }
              >
                <RegionSelect
                  id={`${ids}-default-region`}
                  provider={providerOf(form.defaultProvider)}
                  value={form.defaultRegion}
                  onChange={(value) => update({ defaultRegion: value })}
                />
              </Field>
            </div>
          ) : (
            <dl className="settings-dl">
              <dt>{s.defaultProvider}</dt>
              <dd>{base.data.settings.defaultPlacement?.provider ?? strings.common.dash}</dd>
              <dt>{s.defaultRegion}</dt>
              <dd>{base.data.settings.defaultPlacement?.region ?? strings.common.dash}</dd>
            </dl>
          )}
          {fixed && vault && form.defaultRegion && form.defaultRegion !== vault.region && (
            <p className="callout callout-info mt-3" data-testid="vault-stays">
              <Lock aria-hidden className="size-4 shrink-0" />
              {s.vaultStays(
                vault.region,
                joinNames([...new Set(appRegions.length ? appRegions : [form.defaultRegion])]),
              )}
            </p>
          )}
          {placementTable()}
          {operate && replaced.length > 0 && (
            <div role="status" className="callout callout-danger mt-3" data-testid="placement-consequences">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              <div>
                <p className="font-semibold">{s.consequencesTitle(moveRegion, replaced.length)}</p>
                <ul className="mt-1 list-disc pl-5">
                  {impactSentences(
                    replaced.map((r) => ({ id: r.resourceId, impacts: r.replaceImpacts })),
                    moveRegion,
                  ).map((sentence) => (
                    <li key={sentence}>{sentence}</li>
                  ))}
                  {resolution.map((d, i) => (
                    <li key={`d${i}`}>{d.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {operate && replaced.length === 0 && resolution.length > 0 && (
            <div role="status" className="callout callout-warning mt-3" data-testid="placement-problems">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              <ul className="list-disc pl-5">
                {resolution.map((d, i) => (
                  <li key={i}>{d.message}</li>
                ))}
              </ul>
            </div>
          )}
        </SettingsCard>

        {showNetwork && (
          <SettingsCard ids={ids} changed={changed} id="network">
            <NetworkSection
              deployment={base.data}
              form={form}
              setForm={setForm}
              impact={dry.data ? (dry.data.networkImpact ?? null) : undefined}
              impactPending={dry.pending}
              impactFailed={!!dry.error}
              diagnostics={[
                ...(dry.data?.networkImpact?.diagnostics ?? []),
                ...settingsDiagnostics.filter(
                  (d) =>
                    isNetworkPath(d.path ?? '') &&
                    !(dry.data?.networkImpact?.diagnostics ?? []).some(
                      (x) => x.code === d.code && x.path === d.path,
                    ),
                ),
              ]}
              problem={networkProblem}
              detach={detach}
              operate={operate}
              onReleased={(released) => {
                setBase(released);
                setSeen(released);
                setFormState(toForm(released.data.name, released.data.settings, definitions));
                onSaved(released);
              }}
            />
          </SettingsCard>
        )}

        <SettingsCard ids={ids} changed={changed} id="parameters">
          {Object.keys(definitions).length === 0 ? (
            <p className="help">{strings.settings.noParameters}</p>
          ) : operate ? (
            <div className="grid max-w-2xl gap-x-3 sm:grid-cols-2">
              {Object.entries(definitions).map(([name, definition]) => (
                <ParameterField
                  key={name}
                  id={`${ids}-param-${name}`}
                  name={name}
                  definition={definition}
                  value={form.parameters[name] ?? ''}
                  errors={errorsAt(`/settings/parameters/${name}`)}
                  onChange={(value) =>
                    setForm((f) => ({ ...f, parameters: { ...f.parameters, [name]: value } }))
                  }
                />
              ))}
            </div>
          ) : (
            <dl className="settings-dl">
              {Object.entries(definitions).map(([name, definition]) => (
                <div key={name} className="contents">
                  <dt className="font-mono">{name}</dt>
                  <dd>{jsonText(base.data.settings.parameters[name] ?? definition.default ?? null)}</dd>
                </div>
              ))}
            </dl>
          )}
        </SettingsCard>

        <SettingsCard ids={ids} changed={changed} id="accounts">
          {operate && accounts.data ? (
            <div className="max-w-md">
              <AccountFields
                idPrefix={ids}
                providers={providers}
                used={used}
                accounts={accounts.data}
                form={form}
                setForm={setForm}
                errorsAt={errorsAt}
                admin={admin}
              />
            </div>
          ) : (
            <dl className="settings-dl">
              {Object.entries(base.data.settings.cloudAccounts).map(([provider, id]) => (
                <div key={provider} className="contents">
                  <dt>{providerOf(provider)?.displayName ?? provider}</dt>
                  <dd>{accountName(id)}</dd>
                </div>
              ))}
            </dl>
          )}
        </SettingsCard>

        <SettingsCard
          ids={ids}
          changed={changed}
          id="vault"
          badge={
            fixed ? (
              <span className="badge tone-neutral">
                <Lock aria-hidden className="size-3" />
                {s.fixed}
              </span>
            ) : undefined
          }
        >
          {vaultProblem && (
            <p role="alert" className="callout callout-danger mb-3" data-testid="vault-problem">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              {vaultProblem}
            </p>
          )}
          {operate && (
            <VaultModeField
              idPrefix={ids}
              form={form}
              update={update}
              providers={providers}
              effective={base.data.effectiveVaultMode ?? null}
              fixedText={
                fixed && vault && !vaultOverview?.outlivesVault ? s.fixedText(vault.name, vault.region) : null
              }
              storeWarning={
                fixed && vault?.state === 'live' && vaultOverview?.outlivesVault
                  ? {
                      text: strings.vaults.willBeDeleted(
                        base.data.name,
                        locationLabel({
                          type: vaultType ?? '',
                          secret: true,
                          label: vaultOverview.label ?? '',
                          container: null,
                        }),
                        vaultOverview.deletedRetentionDays ?? 0,
                      ),
                      danger: (vaultOverview.deletedRetentionDays ?? 0) === 0,
                    }
                  : null
              }
              errors={errorsAt('/settings/vaultMode')}
            />
          )}
          {fixed && vault ? (
            <div data-testid="vault-fixed">
              <dl className="settings-dl">
                <dt>{s.vault}</dt>
                <dd>
                  {locationLabel({
                    type: vaultType ?? 'azure-key-vault',
                    secret: true,
                    label: '',
                    container: null,
                  })}{' '}
                  <span className="font-mono">{vault.name}</span>
                </dd>
                <dt>{s.vaultPlacement}</dt>
                <dd>
                  {vault.provider} · <span className="font-semibold">{vault.region}</span>
                </dd>
                {vault.state === 'soft-deleted' && (
                  <>
                    <dt>{s.state}</dt>
                    <dd data-testid="vault-soft-deleted">{s.softDeleted(formatDay(vault.purgeAfter))}</dd>
                  </>
                )}
                <dt>{s.secretStore}</dt>
                <dd>{s.storeOnlyWithoutVault(base.data.settings.secretStore)}</dd>
              </dl>
              {vault.check?.error && (
                <p className="callout callout-warning mt-2" data-testid="vault-check-error">
                  <Info aria-hidden className="size-4 shrink-0" />
                  {s.couldNotCheck(vault.check.error.message)}
                </p>
              )}
              <p className="help mt-2">{s.fixedText(vault.name, vault.region)}</p>
              <FieldMessagesList
                messages={[...errorsAt('/settings/vault/provider'), ...errorsAt('/settings/vault/region')]}
              />
            </div>
          ) : operate ? (
            <div className="max-w-2xl">
              {vaultPurged && (
                <p className="callout callout-info mb-3" data-testid="vault-purged-note">
                  <Info aria-hidden className="size-4 shrink-0" />
                  {s.vaultPurged(formatDay(vaultPurged.checkedAt))}
                </p>
              )}
              {runVaultRegion !== null && (
                <p className="callout callout-info mb-3" data-testid="vault-run-active">
                  <Lock aria-hidden className="size-4 shrink-0" />
                  {s.vaultRunActive(runVaultRegion)}
                </p>
              )}
              <VaultFields
                idPrefix={ids}
                form={form}
                update={update}
                providers={providers}
                errorsAt={errorsAt}
              />
              <SecretStoreField
                id={`${ids}-store`}
                value={form.secretStore}
                stores={stores.data ?? []}
                onChange={(value) => update({ secretStore: value })}
                errors={errorsAt('/settings/secretStore')}
              />
            </div>
          ) : (
            <dl className="settings-dl">
              <dt>{s.vaultPlacement}</dt>
              <dd>
                {[
                  base.data.settings.vault?.provider ?? base.data.settings.defaultPlacement?.provider,
                  base.data.settings.vault?.region ?? base.data.settings.defaultPlacement?.region,
                ]
                  .filter(Boolean)
                  .join(' · ') || strings.common.dash}
              </dd>
              <dt>{s.secretStore}</dt>
              <dd>{base.data.settings.secretStore}</dd>
            </dl>
          )}
        </SettingsCard>

        <SettingsCard ids={ids} changed={changed} id="failure">
          {operate ? (
            <FailurePolicyField
              name={`${ids}-onFailure`}
              value={form.onFailure}
              onChange={(value) => update({ onFailure: value })}
              errors={errorsAt('/settings/onFailure')}
            />
          ) : (
            <p>
              {base.data.settings.onFailure === 'rollback'
                ? strings.settings.rollback
                : strings.settings.stop}
            </p>
          )}
        </SettingsCard>
      </fieldset>

      {operate && changes.length > 0 && (
        <div role="region" aria-label={s.saveBar} className="save-bar" data-testid="save-bar">
          <Info aria-hidden className="size-4 shrink-0 text-accent-fg" />
          <p className="min-w-0 flex-1 text-sm">
            <span className="font-semibold">{s.changes(barTexts.length)}</span>
            {' · '}
            {barTexts.slice(0, 2).join(', ')}
            {barTexts.length > 2 && ` ${s.more(barTexts.length - 2)}`}
            {' · '}
            {s.takesEffect}
            {statefulReplaced && `; ${s.typedNote(form.name || base.data.name)}`}
          </p>
          <Button onClick={discard} disabled={busy}>
            {s.discard}
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={busy}>
            {busy ? s.saving : s.save}
          </Button>
        </div>
      )}
      {blocker.state === 'blocked' && (
        <Dialog
          title={s.leaveTitle}
          onClose={() => blocker.reset()}
          footer={
            <>
              <Button onClick={() => blocker.reset()}>{strings.designer.stay}</Button>
              <Button variant="danger" onClick={() => blocker.proceed()}>
                {strings.designer.leave}
              </Button>
            </>
          }
        >
          <p data-autofocus tabIndex={-1}>
            {s.leaveMessage}
          </p>
        </Dialog>
      )}
    </div>
  );
}

function FieldMessagesList({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <ul className="field-error mt-2">
      {messages.map((m, i) => (
        <li key={i}>{m}</li>
      ))}
    </ul>
  );
}

function SettingsCard({
  ids,
  id: key,
  changed,
  children,
  badge,
}: {
  ids: string;
  id: SettingsSection;
  changed: ReadonlySet<SettingsSection>;
  children: ReactNode;
  badge?: ReactNode;
}) {
  return (
    <section
      id={`${ids}-${key}`}
      aria-labelledby={`${ids}-${key}-title`}
      className="settings-card"
      data-settings-section={key}
    >
      <div className="settings-card-head">
        <h2 id={`${ids}-${key}-title`} className="text-[15px] font-semibold">
          {titleOf(key)}
        </h2>
        {changed.has(key) && <span className="badge tone-info">{s.changed}</span>}
        {badge}
      </div>
      <div className="settings-card-body">{children}</div>
    </section>
  );
}
